import type {
  EvidenceOut,
  FieldResultOut,
  ReviewCreate,
} from "@platform/api/golden";

export type DiscrepancyVerdict = ReviewCreate["verdict"];

export const VERDICT_LABELS: Readonly<Record<DiscrepancyVerdict, string>> = {
  rumi_correct: "RUMI is right (the analyst was wrong)",
  analyst_correct: "The analyst is right (RUMI is wrong)",
  both_wrong: "Both are wrong",
  equivalent: "The values mean the same thing",
  not_determinable: "The intake data did not contain the answer",
  valid_call_out: "Valid call-out: RUMI was right to ask a person",
  should_have_known: "RUMI should have known this (RUMI is wrong)",
};

// The order the verdicts are offered in.
const VERDICTS: readonly DiscrepancyVerdict[] = [
  "rumi_correct",
  "analyst_correct",
  "both_wrong",
  "equivalent",
  "not_determinable",
  "valid_call_out",
  "should_have_known",
];

/** RUMI asked a person about the field: one of its comments on it carries needs_review. */
export const askedForHelp = (evidence: readonly EvidenceOut[]): boolean =>
  evidence.some((entry) => entry.needs_review);

/**
 * The call-out verdicts judge RUMI's ask, and each answers it: how the field gets
 * resolved when the ask was warranted, or why RUMI could have known.
 */
export const answersAnAsk = (verdict: DiscrepancyVerdict): boolean =>
  verdict === "valid_call_out" || verdict === "should_have_known";

const REASON_PROMPTS: Partial<Record<DiscrepancyVerdict, string>> = {
  valid_call_out: "How would you resolve it?",
  should_have_known: "Why should RUMI have known?",
};

export const reasonPrompt = (verdict: DiscrepancyVerdict): string =>
  REASON_PROMPTS[verdict] ?? "Reason (evidence)";

export const REASON_REQUIRED = "A verdict on RUMI's ask needs its answer";

/** The verdict a form opens on: the call to make about RUMI's ask when it asked. */
export const defaultVerdict = (
  evidence: readonly EvidenceOut[],
): DiscrepancyVerdict =>
  askedForHelp(evidence) ? "valid_call_out" : "analyst_correct";

/**
 * The verdicts the API accepts for a discrepancy, given RUMI's evidence on it: an
 * unexpected entity has no baseline path to correct (no both_wrong), a field RUMI left
 * empty has no value to accept as equivalent (RUMI being right about it makes the
 * answer key expect it empty), and the two call-out verdicts judge an ask RUMI made,
 * so they need one.
 */
export const verdictsFor = (
  result: FieldResultOut,
  evidence: readonly EvidenceOut[],
): readonly DiscrepancyVerdict[] => {
  const isUnexpected = result.outcome === "unexpected";
  const rumiLeftItEmpty = !isUnexpected && result.rumi_value == null;
  const asked = askedForHelp(evidence);
  return VERDICTS.filter((verdict) => {
    if (isUnexpected && verdict === "both_wrong") return false;
    if (rumiLeftItEmpty && verdict === "equivalent") return false;
    if (!asked && answersAnAsk(verdict)) return false;
    return true;
  });
};
